import { VFXSystem } from './VFXSystem';
import { gameLogger } from '../../utils/GameLogger';
import { VisualLifecycleStore } from '../diagnostics/VisualLifecycleStore';
import type { IHitPresentationTarget } from '../../shared/combat/hitPresentation';
import { getMotionPrefs } from '../systems/accessibility/motionPrefs';
import { normalizeHitWeight, newHitCorrelationId, type HitWeight, type LegacyHitWeight } from '../../shared/combat/hitWeight';
import { HIT_FEEL_TIERS } from '../../shared/config/hit-feel.config';
import { authoredImpactBurstId } from './authoredImpact';

/** What the optimistic hit path already did for a hit, so the
 *  server-confirmed path can dedup instead of double-firing.
 *  `feelApplied` = the optimistic play actually fired hit-feel (a
 *  def-level `screenShake`, or the crit_overlay stack) — K23
 *  one-feel-owner rule, docs/plans/fx-grammar-specs/hit-feel-kit.md §2.2. */
interface OptimisticImpactNote {
  ts: number;
  feelApplied: boolean;
}

/**
 * CombatFeedbackManager - A high-level facade for combat visuals and game feel.
 * 
 * It coordinates VFX, particles, screen effects, and time scaling
 * to provide satisfying feedback for combat and game events.
 */
export class CombatFeedbackManager {
  private static instance: CombatFeedbackManager;
  private scene: unknown | null = null;
  private vfxSystem: VFXSystem | null = null;

  /** Optimistic hit-effect plays by `${targetId}:${abilityId}` → FIFO notes.
   *  The local attacker's optimistic path (CombatEventVFXHandler.onDamageDealt)
   *  already splashed this hit; the server-confirmed triggerImpact must not
   *  splash it AGAIN (one hit rendered two overlapping impact effects).
   *  K23 (hit-feel-kit spec §2.2): when the optimistic play ALSO fired
   *  hit-feel (def-level screenShake / crit_overlay stack), the confirmed
   *  tier shake is deduped too — one feel owner per hit. Confirmed stays
   *  the fallback feel owner for feel-less optimistic plays, so no hit
   *  goes numb. The wire has no per-hit correlation id, so FIFO preserves
   *  multiple same-key hits instead of overwriting them while delivery stays
   *  ordered. */
  private recentOptimisticImpacts = new Map<string, OptimisticImpactNote[]>();
  private static readonly OPTIMISTIC_DEDUP_WINDOW_MS = 700;
  private static readonly MAX_OPTIMISTIC_NOTES_PER_KEY = 32;

  private constructor() {}

  /**
   * The local player's own hit applied its tier feel as it touched (predictedHitFeel): the confirmed
   * hit skips its shake, freeze, target flash/nudge and big-hit effects. Marks the newest note for the
   * hit, or adds one when the hit played no impact effect of its own.
   */
  public markOptimisticFeel(targetId: string | undefined, abilityId: string | null | undefined): void {
    if (!targetId) return;
    const key = `${targetId}:${abilityId ?? ''}`;
    const queue = this.recentOptimisticImpacts.get(key) ?? [];
    this.pruneExpiredOptimisticNotes(queue, Date.now());
    const newest = queue[queue.length - 1];
    if (newest && !newest.feelApplied) newest.feelApplied = true;
    else queue.push({ ts: Date.now(), feelApplied: true });
    this.recentOptimisticImpacts.set(key, queue);
  }

  /** Called by the optimistic hit path after it plays an impact effect. */
  public noteOptimisticImpact(targetId: string | undefined, abilityId: string | null | undefined): void {
    if (!targetId) return;
    const now = Date.now();
    const key = `${targetId}:${abilityId ?? ''}`;
    const queue = this.recentOptimisticImpacts.get(key) ?? [];
    this.pruneExpiredOptimisticNotes(queue, now);
    // Plan 29 F8: a VFX def can no longer shake or flash the screen
    // (screenFeelScope.applyDefScreenFeel), so the effect play owns no feel.
    // The local player's own hit can, as it touches (markOptimisticFeel).
    queue.push({ ts: now, feelApplied: false });
    if (queue.length > CombatFeedbackManager.MAX_OPTIMISTIC_NOTES_PER_KEY) {
      queue.shift();
    }
    this.recentOptimisticImpacts.set(key, queue);
    // Opportunistic sweep — the map only ever holds a few in-flight hits.
    if (this.recentOptimisticImpacts.size > 32) {
      for (const [queuedKey, notes] of this.recentOptimisticImpacts) {
        this.pruneExpiredOptimisticNotes(notes, now);
        if (notes.length === 0) this.recentOptimisticImpacts.delete(queuedKey);
      }
    }
  }

  private pruneExpiredOptimisticNotes(notes: OptimisticImpactNote[], now: number): void {
    while (
      notes.length > 0
      && now - notes[0].ts > CombatFeedbackManager.OPTIMISTIC_DEDUP_WINDOW_MS
    ) {
      notes.shift();
    }
  }

  private consumeOptimisticImpact(
    targetId: string | undefined,
    abilityId: string | null | undefined,
  ): OptimisticImpactNote | null {
    if (!targetId) return null;
    const key = `${targetId}:${abilityId ?? ''}`;
    const queue = this.recentOptimisticImpacts.get(key);
    if (queue === undefined) return null;
    this.pruneExpiredOptimisticNotes(queue, Date.now());
    const note = queue.shift() ?? null;
    if (queue.length === 0) this.recentOptimisticImpacts.delete(key);
    return note;
  }

  public static getInstance(): CombatFeedbackManager {
    if (!CombatFeedbackManager.instance) {
      CombatFeedbackManager.instance = new CombatFeedbackManager();
    }
    return CombatFeedbackManager.instance;
  }

  public init(scene: unknown, vfxSystem: VFXSystem): void {
    this.scene = scene;
    this.vfxSystem = vfxSystem;
    gameLogger.info('vfx', '[CombatFeedbackManager] Initialized');
  }

  /**
   * Impact feedback for hits, combining multiple systems.
   */
  public triggerImpact(x: number, y: number, options: {
    intensity: HitWeight | LegacyHitWeight,
    type?: 'physical' | 'fire' | 'ice' | 'lightning' | 'arcane' | 'holy' | 'shadow' | 'nature',
    targetId?: string,
    targetPresentation?: IHitPresentationTarget | null,
    /** Originating ability — threaded onto the lifecycle event so dedup
     *  can key by (target, ability, school) and the digest reads clearly. */
    abilityId?: string | null,
    /** Pipeline path that requested this impact (e.g. 'confirmed'). */
    source?: string | null,
    /** False: apply the feel only, no impact effect (the hit's authored impact already played). */
    effect?: boolean,
    /** False when neither side of the hit is the local player: the hit still
     *  draws on its target, but shakes/freezes/flashes no screen (screenFeelScope). */
    screenFeel?: boolean,
    /** Lane AZ (plan 23 row 50): hit-stop is for crits only. `false` drops the
     *  freeze (screen seam and target pose) for a normal hit; omitted keeps the
     *  tier's freeze, for callers that do not know. */
    critical?: boolean,
  }): void {
    if (!this.vfxSystem || !this.scene) return;

    const { intensity: rawIntensity, type = 'physical', targetId, targetPresentation, abilityId, source } = options;
    const screenFeel = options.screenFeel !== false;
    const intensity = normalizeHitWeight(rawIntensity);

    // Server-confirmed impact for a hit the optimistic path already splashed:
    // skip the duplicate effect play. K23 one-feel-owner (hit-feel-kit spec
    // §2.2): when the optimistic play also fired hit-feel (def-level
    // screenShake / crit_overlay stack), skip the tier shake too — the
    // optimistic path owned the feel for this hit. Confirmed remains the
    // fallback owner when the optimistic play was feel-less (or for hits
    // never predicted locally), so no hit goes numb.
    const optimisticNote = source === 'confirmed'
      ? this.consumeOptimisticImpact(targetId, abilityId)
      : null;
    const effectDeduped = optimisticNote !== null;
    const feelDeduped = !!optimisticNote?.feelApplied;
    // Tripwire surface (spec §6): feelOwner makes "exactly one hit-feel per
    // hit" observable in sidecar data — 'optimistic' means the shake already
    // fired at prediction time and was suppressed here.
    const feelOwner = feelDeduped ? 'optimistic' : 'confirmed';

    VisualLifecycleStore.record({
      type: 'impact_feedback',
      label: `${intensity} ${type} ${targetId ? '→ ' + targetId : ''}${effectDeduped ? ' (effect deduped)' : ''}${feelDeduped ? ' (feel deduped)' : ''}`,
      x, y,
      targetId: targetId || null,
      abilityId: abilityId ?? null,
      school: type,
      source: source ?? null,
      meta: { intensity: rawIntensity, weight: intensity, type, hasSprite: !!targetPresentation, effectDeduped, feelDeduped, feelOwner },
    });

    const tier = HIT_FEEL_TIERS[intensity];
    const freezeMs = options.critical === false ? 0 : tier.freezeMs;

    // 1. Visual Effect (Particles/Primitives)
    if (!effectDeduped && options.effect !== false) {
      let effectId = 'hit_spark';
      if (type !== 'physical') {
        effectId = `hit_${type}`;
      }

      // Repeated siphon ticks need a compact contact rim, including when the
      // area sentinel cannot dedup against a confirmed NPC. Keep hit feel intact.
      const drainLife = abilityId === 'drain_life';
      // Plan 29 F9: an unpredicted confirmed hit (area tick, zone, spin)
      // plays the ability's authored impact burst, not the school splash.
      const authored = drainLife ? undefined : authoredImpactBurstId(abilityId);
      this.vfxSystem.playById(drainLife ? 'drain_life_tick_impact' : authored ?? effectId, x, y, {
        scale: drainLife || authored ? 1 : tier.effectScale, targetId,
      });
    }

    // 2. Screen Shake — skipped when the optimistic path already owned the
    // feel for this hit (K23 double-shake fix, spec §1.7).
    let shakeApplied = false;
    let shakeReason: 'applied' | 'feel-deduped' | 'none' | 'camera-rejected' = 'none';
    if (tier.shake && screenFeel) {
      if (feelDeduped) {
        shakeReason = 'feel-deduped';
      } else {
        shakeApplied = Boolean(this.vfxSystem.screenShake(tier.shake.intensity, tier.shake.durationMs));
        shakeReason = shakeApplied ? 'applied' : 'camera-rejected';
      }
    }

    // 3. Hitstop (Freeze-frame) — currently an honest no-op (K22): the
    // Phaser-era global freeze is dead on the Pixi substrate. The call site
    // stays as the seam for the per-sprite freeze store (spec §7 step 5).
    if (freezeMs > 0 && !feelDeduped && screenFeel) {
      this.vfxSystem.hitstop(freezeMs);
    }

    // 4. Renderer-owned target reaction. Directly tweening an entity's x/y or
    // clearing its tint here fights authoritative interpolation, authored mob
    // poses, hover state, and death/base tints. The renderer owns those rules
    // and reports which components it actually applied.
    // Skipped when the local hit already flashed and nudged its target as it touched.
    if (targetPresentation && !feelDeduped) {
      const snap = targetPresentation.snapshot();
      if (snap.activity === 'active') {
        const flashColor = tier.flash?.colorMode === 'white' ? 0xffffff : 0xff0000;
        const correlationId = newHitCorrelationId();
        const reaction = targetPresentation.applyHitPresentation({
          correlationId,
          source: source === 'optimistic' ? 'optimistic' : 'confirmed',
          weight: intensity,
          direction: null,
          flash: tier.flash
            ? { requested: true, color: flashColor, durationMs: tier.flash.durationMs }
            : { requested: false, color: 0, durationMs: 0 },
          freeze: { requestedMs: freezeMs },
          nudge: { requestedPx: tier.nudgePx },
          reducedMotion: getMotionPrefs().reduceMotion,
        });
        VisualLifecycleStore.record({
          type: 'hit_feel',
          label: `${intensity} feel`,
          x: snap.worldAnchor.x,
          y: snap.worldAnchor.y,
          targetId: targetId || null,
          abilityId: abilityId ?? null,
          school: type,
          source: source ?? null,
          meta: {
            correlationId,
            weight: intensity,
            shake: {
              requested: tier.shake !== null,
              applied: shakeApplied,
              reason: shakeReason,
            },
            flash: reaction.flash,
            freezeMs: reaction.freezeMs,
            nudgePx: reaction.nudgePx,
            activity: reaction.activity,
          },
        });
      }
    }

    // 5. Special Effects for massive hits
    if (intensity === 'epic' && screenFeel && !feelDeduped) {
      this.vfxSystem.screenFlash?.(0xffffff, 150);
      this.vfxSystem.chromaticAberration?.(0.6, 250);
      this.vfxSystem.vignette?.(0.7, 400);
    }
  }

  /**
   * Apply a persistent status effect visual to an entity.
   */
  public applyStatusEffect(entityId: string, effectId: 'burning' | 'frozen' | 'stunned' | 'shielded', overrides?: any): void {
    if (!this.vfxSystem) return;
    const instanceId = `${entityId}_${effectId}`;
    this.vfxSystem.getEffectPlayer().playPersistent(instanceId, effectId, 0, 0, { targetId: entityId, ...overrides });
    gameLogger.debug('vfx', `[CombatFeedback] Applied status: ${effectId} to ${entityId}`);
  }

  /**
   * Remove a persistent status effect visual from an entity.
   */
  public removeStatusEffect(entityId: string, effectId: string): void {
    if (!this.vfxSystem) return;
    const instanceId = `${entityId}_${effectId}`;
    this.vfxSystem.getEffectPlayer().stopPersistentEffect(instanceId);
    gameLogger.debug('vfx', `[CombatFeedback] Removed status: ${effectId} from ${entityId}`);
  }

  /**
   * High-impact feedback for defeating a boss or elite.
   */
  public triggerFinisher(x: number, y: number): void {
    if (!this.vfxSystem) return;

    this.vfxSystem.hitstop(200);
    this.vfxSystem.slowMo(0.2, 800);
    this.vfxSystem.screenFlash(0xffffff, 300);
    this.vfxSystem.chromaticAberration(1.0, 1000);
    this.vfxSystem.vignette(1.0, 1500);
    
    // Spawn massive burst
    this.vfxSystem.playById('finisher_burst', x, y, { scale: 2 });
  }

  /**
   * Feedback for gaining items or gold
   */
  public triggerReward(x: number, y: number, text: string): void {
    if (!this.vfxSystem) return;

    this.vfxSystem.playById('sparkle_burst', x, y);
    this.vfxSystem.showFloatingText(text, x, y, {
      color: '#ffcc00',
      fontSize: '20px',
      style: 'item',
      bounce: true
    } as any);
  }

  /**
   * Show a data-driven animated text effect (typewriter, pop, shake, etc.).
   * Thin wrapper to VFXSystem.showAnimatedText so combat code doesn't need
   * to reach past the feedback facade.
   */
  public showAnimatedText(
    text: string,
    x: number,
    y: number,
    options: Parameters<NonNullable<typeof this.vfxSystem>['showAnimatedText']>[3],
  ): void {
    if (!this.vfxSystem) return;
    this.vfxSystem.showAnimatedText(text, x, y, options);
  }

  /**
   * Dramatic screen-space announcement (e.g. "VICTORY", "BOSS PHASE 2").
   */
  public triggerAnnouncement(text: string, x: number, y: number, options?: Partial<{
    color: number;
    durationMs: number;
  }>): void {
    if (!this.vfxSystem) return;
    this.vfxSystem.showAnimatedText(text, x, y, {
      animation: ['pop', 'pulse', 'float'],
      durationMs: options?.durationMs ?? 2500,
      style: {
        fontSize: 32,
        color: options?.color ?? 0xffdd44,
        fontWeight: 'bold',
        strokeWidth: 4,
        anchor: { x: 0.5, y: 0.5 },
        screenSpace: true,
      },
      holdMs: 800,
    });
  }
}

// Export a singleton helper
export const CombatFeedback = CombatFeedbackManager.getInstance();
